import classNames from "classnames";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Props = {
  actionsList: {
    name: string;
    action: () => void;
    icon?: React.ReactNode;
    extraClasses?: string;
    iconColor?: string;
    disabled?: boolean;
  }[];
};

type Coords = {
  top: number;
  left: number;
  placement: "top" | "bottom";
};

export const SettingsDropdown: React.FC<React.PropsWithChildren<Props>> = ({
  actionsList,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<Coords>({
    top: 0,
    left: 0,
    placement: "bottom",
  });

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const GAP = 4;

  const updatePosition = () => {
    if (!triggerRef.current || !menuRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    const shouldOpenTop =
      spaceBelow < menuRect.height + GAP && spaceAbove > menuRect.height + GAP;

    let top = shouldOpenTop
      ? triggerRect.top - menuRect.height - GAP
      : triggerRect.bottom + GAP;

    let left = triggerRect.right - menuRect.width;

    // защита от выхода за экран справа/слева
    if (left < 8) left = 8;
    if (left + menuRect.width > window.innerWidth - 8) {
      left = window.innerWidth - menuRect.width - 8;
    }

    // защита от выхода за экран сверху/снизу
    if (top < 8) top = 8;
    if (top + menuRect.height > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - menuRect.height - 8);
    }

    setCoords({
      top,
      left,
      placement: shouldOpenTop ? "top" : "bottom",
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;

      if (
        target instanceof Node &&
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, actionsList.length]);

  return (
    <>
      <div ref={triggerRef} onClick={() => setIsOpen((prev) => !prev)}>
        {children}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 9999,
            }}
            className={
              "min-w-fit rounded-lg border border-[#E4E9F2] bg-white shadow-lg"
            }>
            {actionsList.map((item) => (
              <div
                key={item.name}
                onClick={() => {
                  if (!item.disabled) {
                    item.action();
                    setIsOpen(false);
                  }
                }}
                className={classNames(
                  "flex cursor-pointer items-center gap-2 px-4 py-2.5 text-[#1D2C3B] transition-all duration-300 hover:bg-[#4C637B0D]",
                  item.disabled && "cursor-not-allowed opacity-50",
                  item.extraClasses ?? "",
                )}>
                {item.icon && (
                  <div
                    className={classNames(
                      item.iconColor ? item.iconColor : "text-[#A1AFC0]",
                    )}>
                    {item.icon}
                  </div>
                )}
                <div className={"text-sm font-semibold"}>{item.name}</div>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};
